import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useVertical } from '../../../context/VerticalContext';
import { Plus, Trash2, Loader2, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { useConfirm } from '../../../components/ui/ConfirmProvider';

interface ServiceTemplate {
    id: string;
    title: string;
    description: string;
    estimated_price: number;
    estimated_duration_minutes: number;
}

export default function ServiceTemplateManager() {
    const { activeOrgId } = useVertical();
    const [templates, setTemplates] = useState<ServiceTemplate[]>([]);
    const confirm = useConfirm();
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<Partial<ServiceTemplate>>({});
    const [isCreating, setIsCreating] = useState(false);

    useEffect(() => {
        if (activeOrgId) fetchTemplates();
    }, [activeOrgId]);

    const fetchTemplates = async () => {
        try {
            const { data, error } = await supabase
                .from('service_templates')
                .select('*')
                .eq('org_id', activeOrgId)
                .order('title');

            if (error) throw error;
            setTemplates(data || []);
        } catch (err) {
            console.error('Error fetching templates:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!activeOrgId) return;

        try {
            if (isCreating) {
                const { data, error } = await supabase
                    .from('service_templates')
                    .insert([{ ...editForm, org_id: activeOrgId }])
                    .select()
                    .single();

                if (error) throw error;
                setTemplates([...templates, data]);
            } else if (isEditing) {
                const { data, error } = await supabase
                    .from('service_templates')
                    .update(editForm)
                    .eq('id', isEditing)
                    .select()
                    .single();

                if (error) throw error;
                setTemplates(templates.map(t => t.id === isEditing ? data : t));
            }
            setIsEditing(null);
            setIsCreating(false);
            setEditForm({});
        } catch (err) {
            console.error('Error saving template:', err);
        }
    };

    const handleDelete = async (id: string) => {
        if (!await confirm({ title: 'Delete template?', message: 'Jobs already using it keep their line items.', confirmText: 'Delete', isDangerous: true })) return;

        try {
            const { error } = await supabase
                .from('service_templates')
                .delete()
                .eq('id', id);

            if (error) throw error;
            setTemplates(templates.filter(t => t.id !== id));
        } catch (err) {
            console.error('Error deleting template:', err);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-fg">Service Templates</h2>
                <button
                    onClick={() => { setIsCreating(true); setEditForm({}); }}
                    className="flex items-center gap-2 px-4 py-2 fd-btn-primary rounded-lg transition-all font-bold text-sm"
                >
                    <Plus className="w-4 h-4" />
                    New Template
                </button>
            </div>

            {isCreating && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-card border border-line rounded-lg p-4 space-y-4"
                >
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="text-sm font-bold text-fg-3">New Template</h3>
                        <button onClick={() => setIsCreating(false)}><X className="w-4 h-4 text-fg-4" /></button>
                    </div>
                    <input
                        type="text"
                        placeholder="Template Title (e.g. Full Service)"
                        value={editForm.title || ''}
                        onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                        className="w-full bg-scrim/20 border border-tint/10 rounded px-3 py-2 text-fg text-sm"
                    />
                    <textarea
                        placeholder="Description"
                        value={editForm.description || ''}
                        onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                        className="w-full bg-scrim/20 border border-tint/10 rounded px-3 py-2 text-fg text-sm h-20"
                    />
                    <div className="flex gap-4">
                        <input
                            type="number"
                            placeholder="Price (Est)"
                            value={editForm.estimated_price || ''}
                            onChange={e => setEditForm({ ...editForm, estimated_price: parseFloat(e.target.value) })}
                            className="w-1/2 bg-scrim/20 border border-tint/10 rounded px-3 py-2 text-fg text-sm"
                        />
                        <input
                            type="number"
                            placeholder="Duration (Mins)"
                            value={editForm.estimated_duration_minutes || ''}
                            onChange={e => setEditForm({ ...editForm, estimated_duration_minutes: parseInt(e.target.value) })}
                            className="w-1/2 bg-scrim/20 border border-tint/10 rounded px-3 py-2 text-fg text-sm"
                        />
                    </div>
                    <button onClick={handleSave} className="w-full py-2 bg-success-fill text-on-fill rounded text-sm font-bold">Save Template</button>
                </motion.div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {loading ? (
                    <div className="col-span-full flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-fg-4" /></div>
                ) : templates.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-fg-4">No templates found. Create one to get started.</div>
                ) : (
                    templates.map(template => (
                        <div key={template.id} className="bg-card border border-line rounded-lg p-4 group hover:border-accent/50 transition-colors">
                            <div className="flex justify-between items-start mb-2">
                                <h3 className="font-bold text-fg">{template.title}</h3>
                                <div className="flex gap-2 fd-hover-reveal">
                                    <button onClick={() => handleDelete(template.id)} className="text-danger hover:text-danger"><Trash2 className="w-4 h-4" /></button>
                                </div>
                            </div>
                            <p className="text-sm text-fg-3 line-clamp-2 mb-3 h-10">{template.description}</p>
                            <div className="flex justify-between items-center text-xs font-mono text-fg-4 border-t border-tint/5 pt-3">
                                <span>£{template.estimated_price}</span>
                                <span>{template.estimated_duration_minutes}m</span>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
